import AddIcon from '@mui/icons-material/Add';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    Checkbox,
    CircularProgress,
    Container,
    FormControlLabel,
    IconButton,
    InputLabel,
    MenuItem,
    SelectChangeEvent,
    Stack,
    Typography,
} from '@mui/material';
import * as lodash from 'lodash';
import { MuiColorInput } from 'mui-color-input';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { EStatusCode } from '../../api/enum';
import { IResponse } from '../../api/httpClient';
import * as SoilHelper from '../../api/soil/soilHelper';
import {
    drainedCohesion,
    drainedSoilPermeability,
    effectivePorosity,
    frictionAngle,
    hydraulicConductivity,
    remodeledSoilPermeability,
    saturatedUnitWeight,
    soilPD,
    thickness,
    undrainedCohesion,
    unitWeight,
} from '../../conversion/soil';
import { ELanguages, TLanguages, languages } from '../../localization';
import { route } from '../../route/Index';
import DropDown from '../../shared/component/DropDown';
import Input from '../../shared/component/Input';
import { EStatus, EUnitOfMeasure } from '../../shared/enum';
import isAdmin from '../../shared/hoc/isAdmin';
import { ISoil, ISoilPhysicalData, ITranslation } from '../../shared/iProject';
import variable from '../../shared/style/variable';
import { toastAlert } from '../../shared/util';
import { useAppSelector } from '../../store/hook';
import { selectProject } from '../../store/project/projectSlice';
import { LoadingButton } from '@mui/lab';

interface ISoilDetailProps {}

const SoilContainer = styled(Container)`
    padding: 0;
    flex-grow: 1;
`;

let translations: ITranslation[] = [];

let newPhysicalDataMetric: ISoilPhysicalData = {
    unitOfMeasure: EUnitOfMeasure.Metric,
    frictionAngle: 0,
    drainedCohesion: 0,
    undrainedCohesion: 0,
    unitWeight: 0,
    saturatedUnitWeight: 0,
    effectivePorosity: 0,
    drainedSoilPermeability: 0,
    remodeledSoilPermeability: 0,
    thickness: 0,
    hydraulicConductivity: 0,
};

let newPhysicalDataImperial: ISoilPhysicalData = {
    unitOfMeasure: EUnitOfMeasure.Imperial,
    frictionAngle: 0,
    drainedCohesion: 0,
    undrainedCohesion: 0,
    unitWeight: 0,
    saturatedUnitWeight: 0,
    effectivePorosity: 0,
    drainedSoilPermeability: 0,
    remodeledSoilPermeability: 0,
    thickness: 0,
    hydraulicConductivity: 0,
};

let newSoil: ISoil = {
    isBase: true,
    status: EStatus.Test,
    color: 'red',
    soilPhysicalData: [newPhysicalDataMetric, newPhysicalDataImperial],
    translations: [
        {
            iso: 'en',
            name: 'New Soil',
        },
        {
            iso: 'it',
            name: 'New Soil',
        },
    ],
};

const SoilDetail = (props: ISoilDetailProps) => {
    let { param } = useParams();

    const project = useAppSelector(selectProject);

    const navigate = useNavigate();

    const { t } = useTranslation();

    const [soil, setSoil] = React.useState<ISoil | null>(null);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isSaveLoading, setSaveIsLoading] = React.useState(false);
    const [unitOfMeasure, setUnitOfMeasure] = React.useState<EUnitOfMeasure>(EUnitOfMeasure.Metric);

    React.useEffect(() => {
        const fetchSoil = async () => {
            setIsLoading(true);
            if (param === 'newSoil') {
                setSoil(newSoil);
                translations = lodash.cloneDeep(newSoil.translations!);
            } else {
                let soilId = parseInt(param!);
                let soil = await SoilHelper.getSoilByIdAsync(soilId);
                setSoil(soil.data);
                translations = lodash.cloneDeep(soil.data.translations!);
            }
            setIsLoading(false);
        };
        fetchSoil();
    }, []);

    const handleClickSave = async () => {
        let payload: ISoil = {
            ...soil!,
            translations,
        };
        let res: IResponse<ISoil>;
        setSaveIsLoading(true);
        if (param === 'newSoil') {
            res = await SoilHelper.insertSoilAsync(payload);
        } else {
            res = await SoilHelper.updateSoilAsync(payload);
        }
        if (res.status == EStatusCode.Ok) {
            toastAlert('success', t('savedSoil'));
        }
        setSaveIsLoading(false);
    };

    const handleChangeTraslation = (event: React.ChangeEvent<any> | SelectChangeEvent<any>) => {
        let { name, value } = event.target;
        let index = translations.findIndex(x => x.iso === name);
        if (index !== -1) {
            translations[index].name = value;
        } else {
            translations.push({
                iso: name,
                name: value,
            });
        }
    };

    const handleClickRemoveTranslation = (iso: string) => (translations = translations.filter(x => x.iso !== iso));

    const handleChange = (event: React.ChangeEvent<any> | SelectChangeEvent<any>, id: number) => {
        let { name, value } = event.target;
        let t = lodash.cloneDeep(soil);
        let index = t!.soilPhysicalData?.findIndex(x => x.id == id)!;
        t!.soilPhysicalData![index] = {
            ...t!.soilPhysicalData![index],
            [name]: parseFloat(value),
        };
        setSoil(t);
    };

    return (
        <>
            <Stack direction={'row'} padding={'0 1rem'} alignItems={'center'}>
                <Link to={route.backoffice} style={{ textDecoration: 'none' }}>
                    <Stack alignItems={'center'} direction={'row'}>
                        <ArrowBackIosIcon style={{ fill: variable.greenLight }} />
                        <Typography variant='h6' padding={'1rem 0'} color={variable.greenLight}>
                            {t('back')}
                        </Typography>
                    </Stack>
                </Link>
                <Stack width={'100%'} textAlign={'center'}>
                    <Typography variant='h6' color={variable.greenLight}>
                        {param == 'newSoil' ? t('addNewSoil') : t('updateSoil')}
                    </Typography>
                </Stack>
            </Stack>
            {isLoading && <CircularProgress />}
            {!isLoading && soil && (
                <SoilContainer maxWidth={'md'}>
                    <Stack height={'95%'} direction='column'>
                        <Stack padding={'0 1rem'} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                            <Stack flexDirection={'row'}>
                                <FormControlLabel
                                    control={<Checkbox checked={soil?.status == EStatus.Public} onChange={e => setSoil({ ...soil!, status: e.target.checked ? EStatus.Public : EStatus.Test })} />}
                                    label={t('public')}
                                />
                                <Stack flexDirection={'row'} gap={1}>
                                    <InputLabel style={{ alignContent: 'center', color: 'black' }}>{t('unitOfMeasure')}</InputLabel>
                                    <DropDown value={unitOfMeasure} onChange={e => setUnitOfMeasure(e.target.value as any)}>
                                        <MenuItem value={EUnitOfMeasure.Metric}>{t('metric')}</MenuItem>
                                        <MenuItem value={EUnitOfMeasure.Imperial}>{t('imperial')}</MenuItem>
                                    </DropDown>
                                </Stack>
                            </Stack>
                            <MuiColorInput value={soil?.color!} onChange={color => setSoil({ ...soil!, color })} />
                        </Stack>
                        <Stack height={'100%'} direction={'row'} spacing={1}>
                            <Stack width={'50%'} position='relative'>
                                <Stack variant='scrollable'>
                                    {Object.keys(languages).map(x => (
                                        <Accordion disableGutters>
                                            <AccordionSummary variant='standard' expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                                <Typography>{languages[x as TLanguages]}</Typography>
                                            </AccordionSummary>
                                            <AccordionDetails>
                                                <Stack direction={'column'} spacing={2}>
                                                    <Input defaultValue={soil!.translations!.find(y => y.iso == x)?.name} id={x} label={t('name')} name={x} onChange={handleChangeTraslation} />
                                                    {x !== ELanguages.en && (
                                                        <Stack alignItems={'end'}>
                                                            <IconButton aria-label={`delete-soil-${x}`} onClick={() => handleClickRemoveTranslation(x)}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </Stack>
                                                    )}
                                                </Stack>
                                            </AccordionDetails>
                                        </Accordion>
                                    ))}
                                </Stack>
                            </Stack>
                            <Stack width={'50%'} position='relative'>
                                <Stack variant='scrollable'>
                                    {soil
                                        .soilPhysicalData!.filter(x => x.unitOfMeasure == unitOfMeasure)
                                        .map((x, i) => (
                                            <Accordion disableGutters>
                                                <AccordionSummary variant='standard' expandIcon={<ExpandMoreIcon />} aria-controls='panel1a-content' id='panel1a-header'>
                                                    <Typography>{t(`data ${i}`)}</Typography>
                                                </AccordionSummary>
                                                <AccordionDetails>
                                                    <Input
                                                        id={frictionAngle}
                                                        label={t(frictionAngle, {
                                                            unitOfMeasure: soilPD[frictionAngle]?.unitOfMeasure[unitOfMeasure],
                                                        })}
                                                        isNumber
                                                        name={frictionAngle}
                                                        value={x.frictionAngle}
                                                        onChange={(e: any) => handleChange(e, x.id!)}
                                                    />
                                                    <Input
                                                        id={drainedCohesion}
                                                        label={t(drainedCohesion, {
                                                            unitOfMeasure: soilPD[drainedCohesion]?.unitOfMeasure[unitOfMeasure],
                                                        })}
                                                        isNumber
                                                        name={drainedCohesion}
                                                        value={x.drainedCohesion}
                                                        onChange={(e: any) => handleChange(e, x.id!)}
                                                    />
                                                    <Input
                                                        id={undrainedCohesion}
                                                        label={t(undrainedCohesion, {
                                                            unitOfMeasure: soilPD[undrainedCohesion]?.unitOfMeasure[unitOfMeasure],
                                                        })}
                                                        isNumber
                                                        name={undrainedCohesion}
                                                        value={x.undrainedCohesion}
                                                        onChange={(e: any) => handleChange(e, x.id!)}
                                                    />
                                                    <Input
                                                        id={unitWeight}
                                                        label={t(unitWeight, {
                                                            unitOfMeasure: soilPD[unitWeight]?.unitOfMeasure[unitOfMeasure],
                                                        })}
                                                        isNumber
                                                        name={unitWeight}
                                                        value={x.unitWeight}
                                                        onChange={(e: any) => handleChange(e, x.id!)}
                                                    />
                                                    <Input
                                                        id={saturatedUnitWeight}
                                                        label={t(saturatedUnitWeight, {
                                                            unitOfMeasure: soilPD[saturatedUnitWeight]?.unitOfMeasure[unitOfMeasure],
                                                        })}
                                                        isNumber
                                                        name={saturatedUnitWeight}
                                                        value={x.saturatedUnitWeight}
                                                        onChange={(e: any) => handleChange(e, x.id!)}
                                                    />
                                                    <Input
                                                        id={effectivePorosity}
                                                        label={t(effectivePorosity, {
                                                            unitOfMeasure: soilPD[effectivePorosity]?.unitOfMeasure[unitOfMeasure],
                                                        })}
                                                        isNumber
                                                        name={effectivePorosity}
                                                        value={x.effectivePorosity}
                                                        onChange={(e: any) => handleChange(e, x.id!)}
                                                    />
                                                    <Input
                                                        id={drainedSoilPermeability}
                                                        label={t(drainedSoilPermeability, {
                                                            unitOfMeasure: soilPD[drainedSoilPermeability]?.unitOfMeasure[unitOfMeasure],
                                                        })}
                                                        isNumber
                                                        name={drainedSoilPermeability}
                                                        value={x.drainedSoilPermeability}
                                                        onChange={(e: any) => handleChange(e, x.id!)}
                                                    />
                                                    <Input
                                                        id={remodeledSoilPermeability}
                                                        label={t(remodeledSoilPermeability, {
                                                            unitOfMeasure: soilPD[remodeledSoilPermeability]?.unitOfMeasure[unitOfMeasure],
                                                        })}
                                                        isNumber
                                                        name={remodeledSoilPermeability}
                                                        value={x.remodeledSoilPermeability}
                                                        onChange={(e: any) => handleChange(e, x.id!)}
                                                    />
                                                    <Input
                                                        id={thickness}
                                                        label={t(thickness, {
                                                            unitOfMeasure: soilPD[thickness]?.unitOfMeasure[unitOfMeasure],
                                                        })}
                                                        isNumber
                                                        name={thickness}
                                                        value={x.thickness}
                                                        onChange={(e: any) => handleChange(e, x.id!)}
                                                    />
                                                    <Input
                                                        id={hydraulicConductivity}
                                                        label={t(hydraulicConductivity, {
                                                            unitOfMeasure: soilPD[hydraulicConductivity]?.unitOfMeasure[unitOfMeasure],
                                                        })}
                                                        isNumber
                                                        name={hydraulicConductivity}
                                                        value={x.hydraulicConductivity}
                                                        onChange={(e: any) => handleChange(e, x.id!)}
                                                    />
                                                    {soil.soilPhysicalData!.length > 1 && (
                                                        <IconButton
                                                            style={{ justifyContent: 'end', width: '100%' }}
                                                            aria-label='remove-data-soil'
                                                            onClick={() => setSoil({ ...soil, soilPhysicalData: soil.soilPhysicalData!.filter((y: any, u: any) => u !== i) })}>
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    )}
                                                </AccordionDetails>
                                            </Accordion>
                                        ))}
                                    <IconButton
                                        style={{ justifyContent: 'end' }}
                                        aria-label='remove-image-material'
                                        onClick={() =>
                                            setSoil({
                                                ...soil,
                                                soilPhysicalData: [...soil.soilPhysicalData!, unitOfMeasure == EUnitOfMeasure.Metric ? { ...newPhysicalDataMetric } : { ...newPhysicalDataImperial }],
                                            })
                                        }>
                                        <AddIcon />
                                    </IconButton>
                                </Stack>
                            </Stack>
                        </Stack>
                        <Stack alignItems='end'>
                            <Stack direction={'row'} spacing={2}>
                                <Button variant='white' onClick={() => navigate(route.backoffice)}>
                                    {t('back')}
                                </Button>
                                <LoadingButton loading={isSaveLoading} style={{ overflow: 'hidden' }} variant='orange' onClick={handleClickSave}>
                                    {t('save')}
                                </LoadingButton>
                            </Stack>
                        </Stack>
                    </Stack>
                </SoilContainer>
            )}
        </>
    );
};

export default isAdmin(SoilDetail);
